import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { statsApi, auditApi } from '../../lib/api';
import DashboardLayout from '../../components/DashLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Users,
  FolderKanban,
  ListTodo,
  Activity,
  ArrowRight,
  Clock,
  User,
  Loader2,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentLogs, setRecentLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, logsRes] = await Promise.all([
        statsApi.get(),
        auditApi.list({ limit: 10 }),
      ]);
      setStats(statsRes.data);
      setRecentLogs(logsRes.data);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'create_user':
      case 'create_project':
      case 'create_task':
        return 'text-green-500';
      case 'delete_user':
      case 'delete_project':
      case 'delete_task':
        return 'text-red-500';
      case 'update_user':
      case 'update_project':
      case 'update_task':
        return 'text-amber-500';
      case 'login':
        return 'text-primary';
      default:
        return 'text-muted-foreground';
    }
  };

  const formatAction = (action) => {
    return action
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-dashboard">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Admin Dashboard</h1>
          <p className="text-muted-foreground mt-1">Overview of your CONDENSYS instance</p>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <Card className="bg-[#0A0A0B] border-[#27272A] hover:border-primary/50 transition-colors" data-testid="users-stat-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Users</CardTitle>
              <Users className="h-5 w-5 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">{stats?.total_users || 0}</div>
              <Link
                to="/admin/users"
                className="text-xs text-primary flex items-center gap-1 mt-2 hover:underline"
              >
                Manage users <ArrowRight className="w-3 h-3" />
              </Link>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0B] border-[#27272A] hover:border-primary/50 transition-colors" data-testid="projects-stat-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Projects</CardTitle>
              <FolderKanban className="h-5 w-5 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">{stats?.total_projects || 0}</div>
              <Link
                to="/admin/projects"
                className="text-xs text-primary flex items-center gap-1 mt-2 hover:underline"
              >
                Manage projects <ArrowRight className="w-3 h-3" />
              </Link>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0B] border-[#27272A] hover:border-primary/50 transition-colors" data-testid="tasks-stat-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Tasks</CardTitle>
              <ListTodo className="h-5 w-5 text-amber-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">{stats?.total_tasks || 0}</div>
              <p className="text-xs text-muted-foreground mt-2">Across all projects</p>
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity */}
        <Card className="bg-[#0A0A0B] border-[#27272A]" data-testid="recent-activity-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-white flex items-center gap-2">
                <Activity className="w-5 h-5 text-primary" />
                Recent Activity
              </CardTitle>
              <CardDescription>Latest actions in the system</CardDescription>
            </div>
            <Link to="/admin/audit">
              <Button variant="outline" size="sm" className="border-[#27272A] hover:border-primary/50">
                View All
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px]">
              {recentLogs.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No activity recorded yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {recentLogs.map((log) => (
                    <div
                      key={log.id}
                      className="flex items-start gap-4 p-3 rounded-lg hover:bg-white/5 transition-colors"
                      data-testid={`activity-log-${log.id}`}
                    >
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <User className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white">
                          <span className="font-medium">{log.actor_name || 'Unknown'}</span>
                          {' '}
                          <span className={getActionColor(log.action)}>{formatAction(log.action)}</span>
                          {' '}
                          <span className="text-muted-foreground">
                            ({log.entity_type} {log.entity_id.slice(0, 8)}...)
                          </span>
                        </p>
                        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          {format(parseISO(log.created_at), 'PPp')}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid gap-4 md:grid-cols-3">
          <Link to="/admin/users" className="block">
            <Card className="bg-[#0A0A0B] border-[#27272A] hover:border-primary/50 hover:bg-white/5 transition-all cursor-pointer h-full">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Users className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Manage Users</h3>
                    <p className="text-sm text-muted-foreground">Create, edit, or disable users</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/projects" className="block">
            <Card className="bg-[#0A0A0B] border-[#27272A] hover:border-green-500/50 hover:bg-white/5 transition-all cursor-pointer h-full">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center">
                    <FolderKanban className="w-6 h-6 text-green-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Manage Projects</h3>
                    <p className="text-sm text-muted-foreground">Create and organize projects</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/audit" className="block">
            <Card className="bg-[#0A0A0B] border-[#27272A] hover:border-amber-500/50 hover:bg-white/5 transition-all cursor-pointer h-full">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                    <Activity className="w-6 h-6 text-amber-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Audit Logs</h3>
                    <p className="text-sm text-muted-foreground">View system activity history</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdminDashboard;
